import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import TagBadge from './TagBadge';

/**
 * Tag assignment dropdown for entity detail panels.
 * Shows current tags as badges and a dropdown to add/remove tags.
 *
 * Props:
 *   practiceId   - current practice ID
 *   entityType   - 'patient' | 'provider' | 'appointment_type' | 'appointment'
 *   entityId     - entity ID
 *   canManage    - whether user has tags:manage permission
 *   onTagsChange - callback when tags change (optional)
 */
function TagSelector({ practiceId, entityType, entityId, canManage = false, onTagsChange }) {
  const [tags, setTags] = useState([]);
  const [definitions, setDefinitions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (practiceId && entityType && entityId) {
      loadTags();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId, entityType, entityId]);

  useEffect(() => {
    if (open && definitions.length === 0) {
      loadDefinitions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  async function loadTags() {
    try {
      const res = await api.getEntityTags(practiceId, entityType, entityId);
      if (res.success) {
        setTags(res.data || []);
        onTagsChange?.(res.data || []);
      }
    } catch (err) {
      console.error('Failed to load tags:', err);
    }
  }

  async function loadDefinitions() {
    try {
      const res = await api.getTagDefinitions(practiceId, entityType);
      if (res.success) setDefinitions(res.data || []);
    } catch (err) {
      console.error('Failed to load tag definitions:', err);
    }
  }

  async function handleToggleTag(tagDefId) {
    if (loading) return;
    setLoading(true);
    try {
      const isAssigned = tags.some(t => t.tag_id === tagDefId);
      if (isAssigned) {
        await api.removeTag(practiceId, entityType, entityId, tagDefId);
      } else {
        await api.assignTag(practiceId, entityType, entityId, tagDefId);
      }
      await loadTags();
    } catch (err) {
      console.error('Failed to toggle tag:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleRemoveTag(tagDefId) {
    if (loading) return;
    setLoading(true);
    try {
      await api.removeTag(practiceId, entityType, entityId, tagDefId);
      await loadTags();
    } catch (err) {
      console.error('Failed to remove tag:', err);
    } finally {
      setLoading(false);
    }
  }

  const assignedIds = new Set(tags.map(t => t.tag_id));

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4, position: 'relative' }} ref={dropdownRef}>
      {tags.map(tag => (
        <TagBadge
          key={tag.tag_id}
          name={tag.name}
          color={tag.color}
          onRemove={canManage ? () => handleRemoveTag(tag.tag_id) : undefined}
        />
      ))}
      {canManage && (
        <button
          onClick={() => setOpen(!open)}
          style={{
            background: 'none', border: '1px dashed #cbd5e1', borderRadius: 12,
            padding: '1px 8px', fontSize: 11, color: '#64748b', cursor: 'pointer',
            lineHeight: '16px',
          }}
          title="Add tag"
        >
          + Tag
        </button>
      )}
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 50,
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: 4,
          minWidth: 180, maxHeight: 240, overflowY: 'auto', marginTop: 4,
        }}>
          {definitions.length === 0 && (
            <div style={{ padding: '8px 12px', color: '#94a3b8', fontSize: 12 }}>
              No tags defined. Create tags in Settings.
            </div>
          )}
          {definitions.map(def => {
            const active = assignedIds.has(def.id);
            return (
              <div
                key={def.id}
                onClick={() => handleToggleTag(def.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '6px 10px', cursor: 'pointer', borderRadius: 4,
                  background: active ? '#f1f5f9' : 'transparent',
                  fontSize: 13,
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={e => e.currentTarget.style.background = active ? '#f1f5f9' : 'transparent'}
              >
                <span style={{
                  width: 10, height: 10, borderRadius: '50%',
                  background: def.color, flexShrink: 0,
                }} />
                <span style={{ flex: 1 }}>{def.name}</span>
                {active && <span style={{ color: '#22c55e', fontWeight: 700 }}>&#10003;</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default TagSelector;
